import React, { createContext, useContext, useEffect, useState } from "react"
import { EAppView } from "../Enum/EAppViewType"
import { CustomerEntity } from "../Objects/Customer/CustomerEntity"
import { API_ROUTES } from "../configuration/RoutesApi"
import { GetRequest } from "../services/ApiClientService"
import { ERequestType } from "../Enum/ERequestType"
import { isTokenValid } from "../Objects/Generics/Helps"

export interface AuthState {
  token: string | null
  customer: CustomerEntity | null
  status: boolean
}

interface PublicViewContextContextData {
  currentView: EAppView
  auth: AuthState
  isAuthenticated: boolean
  setCurrentView: (view: EAppView) => void
  updateCustomer: (customer: CustomerEntity) => void
  login: (token: string) => void
  logout: () => void
  reloadCustomer: () => Promise<void>
}

const PublicViewContext = createContext<PublicViewContextContextData | undefined>(undefined)

export const PublicViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentView, setCurrentView] = useState<EAppView>(EAppView.CustomerMenu)

  const [auth, setAuth] = useState<AuthState>({
    token: null,
    customer: null,
    status: false,
  })

  useEffect(() => {
    const token = localStorage.getItem(API_ROUTES.TOKEN_CUSTOMER)

    if (!token) {
      return
    }

    if (!isTokenValid(token)) {
      logout()

      return
    }

    setAuth({
      token,
      customer: null,
      status: true,
    })

    loadCustomer()
  }, [])

  const loadCustomer = async () => {
    try {
      const response = await GetRequest<CustomerEntity>(
        API_ROUTES.CUSTOMER.GET_UPDATED_CUSTOMER_PUBLIC,
        ERequestType.Customer,
      )

      if (!response.success) {
        return
      }

      setAuth((prev) => ({
        ...prev,
        customer: new CustomerEntity(response.data),
        status: true,
      }))
    } catch (error) {
      logout()
    }
  }

  const login = (token: string) => {
    localStorage.setItem(API_ROUTES.TOKEN_CUSTOMER, token)

    setAuth({
      token,
      customer: null,
      status: true,
    })

    loadCustomer()
  }

  const logout = () => {
    localStorage.removeItem(API_ROUTES.TOKEN_CUSTOMER)

    setAuth({
      token: null,
      customer: null,
      status: false,
    })

    setCurrentView(EAppView.CustomerMenu)
  }

  const updateCustomer = (customer: CustomerEntity) => {
    setAuth((prev) => ({
      ...prev,
      customer: new CustomerEntity(customer),
    }))
  }

  const reloadCustomer = async () => {
    if (!auth.token) {
      return
    }

    await loadCustomer()
  }

  return (
    <PublicViewContext.Provider
      value={{
        currentView,
        setCurrentView,
        updateCustomer,
        auth,
        login,
        logout,
        reloadCustomer,
        isAuthenticated: !!auth.token,
      }}
    >
      {children}
    </PublicViewContext.Provider>
  )
}

export const usePublicViewContext = (): PublicViewContextContextData => {
  const context = useContext(PublicViewContext)

  if (!context) {
    throw new Error("usePublicViewContext deve ser usado dentro de PublicViewProvider")
  }

  return context
}
